import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import type { MapData } from '@shared/types/map';
import { TILE_SIZE } from '@shared/constants';
import { WATER_PIPELINE_KEY } from '../../sprites/WaterPipeline';
import { getSurfaceById, getSurfaceTextureKey } from '../../sprites/SurfaceSpriteManager';
import {
  getWaterTypeForSprite,
  getWaterFrameTextureKey,
  getWaterFrameCount,
} from '../../sprites/WaterSpriteManager';

/**
 * Render de tiles del mapa con viewport culling por chunks: construye el índice de tiles
 * (buildMapTileIndex), crea/destruye los GameObjects de tile on-demand según la cámara
 * (createMapTileObject/destroyMapTileObject/updateMapCulling), drena la cola de creación por frame
 * (processTileCreateQueue) y limpia todo al recargar (clearMapTiles). Extraído de GameScene
 * (god-object). Los arrays/Maps de tiles viven en GameScene (host) y se acceden vía `this.scene.*`.
 * Ver docs/WORLD_BUILDER.md (viewport culling).
 */
export class MapTileRenderer {
  constructor(private scene: GameScene) {}

  public chunkKeyForTile(tx: number, ty: number): string {
    const cs = GameScene.MAP_CHUNK_TILES;
    return `${Math.floor(tx / cs)},${Math.floor(ty / cs)}`;
  }

  /**
   * Construye el índice de entradas de tile y los buckets por chunk a partir
   * del MapData. No crea GameObjects.
   */
  public buildMapTileIndex(mapData: MapData): void {
    this.scene.mapTileEntries = [];
    this.scene.waterTileSet = new Set();
    this.scene.shoreBlockSet = new Set();
    this.scene.mapTileChunks = new Map();
    this.scene.activeTileObjects = new Map();
    this.scene.activeMapChunks = new Set();
    this.scene.lastCullCamX = Number.NaN;
    this.scene.lastCullCamY = Number.NaN;

    for (const layer of mapData.layers) {
      if (layer.name.startsWith('_')) continue;
      for (const tile of layer.tiles) {
        const index = this.scene.mapTileEntries.length;
        this.scene.mapTileEntries.push({ tile, layerName: layer.name });
        if (this.scene.mapAmbient.isWaterTile(tile.sprite, layer.name)) {
          this.scene.waterTileSet.add(`${tile.x},${tile.y}`);
        } else if (GameScene.isShoreSurfaceLayer(layer.name)) {
          // Superficie de tierra/costa (pasto/arena, centro o borde difuminado 4×4):
          // marca su bloque como ORILLA. Los tiles de suelo son 4×4 alineados al origen
          // de bloque, pero normalizamos por las dudas.
          const bx = Math.floor(tile.x / 4) * 4;
          const by = Math.floor(tile.y / 4) * 4;
          this.scene.shoreBlockSet.add(`${bx},${by}`);
        }
        const key = this.chunkKeyForTile(tile.x, tile.y);
        let bucket = this.scene.mapTileChunks.get(key);
        if (!bucket) {
          bucket = [];
          this.scene.mapTileChunks.set(key, bucket);
        }
        bucket.push(index);
      }
    }
  }

  /**
   * Crea el GameObject (sprite o shader de agua) de una entrada de tile.
   * Replica la lógica original de render: textura de surface, agua con shader,
   * origin, offset de árboles, luz de decoración, depth y filtro de cámara.
   */
  public createMapTileObject(index: number): void {
    if (this.scene.activeTileObjects.has(index)) return;
    const entry = this.scene.mapTileEntries[index];
    if (!entry) return;
    const { tile, layerName } = entry;

    const isGroundLike =
      layerName === 'ground' ||
      layerName === 'ground_deco_under' ||
      layerName === 'layer1_ground' ||
      layerName === 'layer2_floor_objects';

    // Agua animada por BLOQUE (128px). El generador empuja agua en bloques 4×4
    // alineados, así que renderizamos UN sprite de 128px en el tile origen del
    // bloque (tx%4==0 && ty%4==0) y saltamos los otros 15 tiles del bloque.
    const waterType = this.scene.mapAmbient.isWaterTile(tile.sprite, layerName)
      ? getWaterTypeForSprite(tile.sprite)
      : null;
    if (waterType) {
      if (tile.x % 4 !== 0 || tile.y % 4 !== 0) return; // cubierto por el sprite del bloque
      const blockKey = getWaterFrameTextureKey(waterType, this.scene.waterFrame % getWaterFrameCount(waterType));
      if (!this.scene.textures.exists(blockKey)) return;
      const blockX = tile.x * TILE_SIZE + (4 * TILE_SIZE) / 2;
      const blockY = tile.y * TILE_SIZE + (4 * TILE_SIZE) / 2;
      const waterSprite = this.scene.add.image(blockX, blockY, blockKey);
      waterSprite.setOrigin(0.5, 0.5);
      waterSprite.setDepth(this.scene.getDepthForMapLayer(layerName, tile.x, tile.y, tile.zOverride));
      if (this.scene.waterPipeline) {
        waterSprite.setPipeline(WATER_PIPELINE_KEY);
        // Atenuar el oleaje en la costa: el factor "calma" (0..1) se pasa al shader
        // como tint gris (lee outTint.r). Se calcula POR ESQUINA (no por bloque) y se
        // setea como tint de 4 vértices: la GPU lo interpola suave a través del quad y,
        // como bloques vecinos comparten esquina con el mismo valor, los bordes
        // coinciden → sin costuras cuadradas hacia la costa. Mar abierto = 1 (blanco).
        waterSprite.setTint(
          GameScene.grayCalmTint(this.scene.mapAmbient.cornerCalmFactor(tile.x, tile.y)),
          GameScene.grayCalmTint(this.scene.mapAmbient.cornerCalmFactor(tile.x + 4, tile.y)),
          GameScene.grayCalmTint(this.scene.mapAmbient.cornerCalmFactor(tile.x, tile.y + 4)),
          GameScene.grayCalmTint(this.scene.mapAmbient.cornerCalmFactor(tile.x + 4, tile.y + 4))
        );
      }
      this.scene.configureObjectCamera(waterSprite);
      this.scene.activeTileObjects.set(index, waterSprite);
      this.scene.waterSprites.set(index, { sprite: waterSprite, type: waterType });
      return;
    }

    // Una superficie multi-tile (4×4 = 128px: pasto/arena/nieve/costa) se centra en el
    // CENTRO DEL BLOQUE, igual que el agua (tile + tilesX*TILE_SIZE/2), no en el centro
    // del primer tile. Si no, queda 48px arriba-izquierda respecto del agua y la costa
    // pasto↔agua sale dura en los lados S/E. 1×1 (caminos) y decoraciones → tile+16 (igual).
    const surfaceDef = getSurfaceById(tile.sprite);
    const tilesX = surfaceDef?.tilesX ?? 1;
    const tilesY = surfaceDef?.tilesY ?? 1;
    const worldX = tile.x * TILE_SIZE + (tilesX * TILE_SIZE) / 2;
    const worldY = isGroundLike
      ? tile.y * TILE_SIZE + (tilesY * TILE_SIZE) / 2
      : tile.y * TILE_SIZE + TILE_SIZE; // deco: ancla abajo, 1 tile (sin cambio)

    // Resolver textura (surfaces usan key generada por SurfaceSpriteManager).
    let textureKey = tile.sprite;
    const surfaceTextureKey = getSurfaceTextureKey(tile.sprite);
    if (this.scene.textures.exists(surfaceTextureKey)) {
      textureKey = surfaceTextureKey;
    }
    if (!this.scene.textures.exists(textureKey)) {
      // Sin textura: marcar como "vacío" para no reintentar cada frame.
      return;
    }

    // El agua se renderiza como imagen plana (sin shader): los shaders por tile
    // eran carísimos de crear y colgaban el frame al entrar a zonas de agua.
    const sprite = this.scene.add.image(worldX, worldY, textureKey);
    sprite.setOrigin(0.5, isGroundLike ? 0.5 : 1);

    const decoMeta = this.scene.decorationIndex?.[tile.sprite];
    if (decoMeta?.light && this.scene.lightingSystem) {
      const lightConfig = decoMeta.light;
      const lightId = `deco_${tile.x}_${tile.y}_${tile.sprite}`;
      const colorValue =
        typeof lightConfig.color === 'string'
          ? parseInt(lightConfig.color.replace('0x', ''), 16)
          : lightConfig.color;
      this.scene.lightingSystem.addLightSource(lightId, {
        x: worldX,
        y: worldY,
        radius: lightConfig.radius,
        intensity: lightConfig.intensity,
        color: colorValue,
        flicker: lightConfig.flicker ?? false,
      });
    }

    sprite.setDepth(this.scene.getDepthForMapLayer(layerName, tile.x, tile.y, tile.zOverride));
    // Asignar a la cámara correcta (juego, no UI).
    this.scene.configureObjectCamera(sprite);
    this.scene.activeTileObjects.set(index, sprite);

    // Techos: registrarlos para el efecto de interior. Si el efecto ya está activo y este
    // techo cae dentro del interior, arrancar atenuado (evita pop al recrearse por culling).
    if (layerName === 'roof' || layerName === 'layer4_roof') {
      this.scene.roofObjects.set(index, sprite);
      if (this.scene.interiorLevel > 0.001 && this.scene.mapAmbient.roofIntersectsInterior(sprite)) {
        sprite.setAlpha(1 - (1 - GameScene.ROOF_DIM_ALPHA) * this.scene.interiorLevel);
      }
    }
  }

  /** Destruye el GameObject de una entrada de tile y limpia su luz de decoración. */
  public destroyMapTileObject(index: number): void {
    const obj = this.scene.activeTileObjects.get(index);
    if (!obj) return;
    const entry = this.scene.mapTileEntries[index];
    if (entry) {
      const { tile } = entry;
      const decoMeta = this.scene.decorationIndex?.[tile.sprite];
      if (decoMeta?.light && this.scene.lightingSystem) {
        this.scene.lightingSystem.removeLightSource(`deco_${tile.x}_${tile.y}_${tile.sprite}`);
      }
    }
    obj.destroy();
    this.scene.activeTileObjects.delete(index);
    this.scene.roofObjects.delete(index);
    this.scene.waterSprites.delete(index);
  }

  /**
   * Recalcula qué chunks deben estar instanciados según el viewport de la
   * cámara (+ margen) y crea/destruye los GameObjects necesarios.
   * @param force ignora el throttle por movimiento de cámara.
   */
  public updateMapCulling(force = false): void {
    if (this.scene.mapTileEntries.length === 0) return;
    const cam = this.scene.cameras.main;
    if (!cam) return;

    // Throttle: recalcular si la cámara se movió >= 1 tile o cambió el tamaño
    // del viewport (zoom/resize), o si force.
    const view = cam.worldView;
    if (
      !force &&
      Math.abs(view.x - this.scene.lastCullCamX) < TILE_SIZE &&
      Math.abs(view.y - this.scene.lastCullCamY) < TILE_SIZE &&
      view.width === this.scene.lastCullCamW &&
      view.height === this.scene.lastCullCamH
    ) {
      return;
    }
    this.scene.lastCullCamX = view.x;
    this.scene.lastCullCamY = view.y;
    this.scene.lastCullCamW = view.width;
    this.scene.lastCullCamH = view.height;

    const margin = GameScene.MAP_CULL_MARGIN_TILES * TILE_SIZE;
    const minTileX = Math.floor((view.x - margin) / TILE_SIZE);
    const minTileY = Math.floor((view.y - margin) / TILE_SIZE);
    const maxTileX = Math.ceil((view.right + margin) / TILE_SIZE);
    const maxTileY = Math.ceil((view.bottom + margin) / TILE_SIZE);

    const cs = GameScene.MAP_CHUNK_TILES;
    const minCx = Math.floor(minTileX / cs);
    const minCy = Math.floor(minTileY / cs);
    const maxCx = Math.floor(maxTileX / cs);
    const maxCy = Math.floor(maxTileY / cs);

    const needed = new Set<string>();
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        needed.add(`${cx},${cy}`);
      }
    }

    // Destruir chunks que salieron de vista.
    for (const key of this.scene.activeMapChunks) {
      if (!needed.has(key)) {
        const bucket = this.scene.mapTileChunks.get(key);
        if (bucket) {
          for (const idx of bucket) this.destroyMapTileObject(idx);
        }
        this.scene.activeMapChunks.delete(key);
      }
    }

    // Encolar chunks nuevos visibles. La creación real se amortiza por frame en
    // processTileCreateQueue() para no colgar el frame al cruzar un chunk. En la
    // carga inicial (force) se crean de una para que el mapa aparezca completo.
    for (const key of needed) {
      if (this.scene.activeMapChunks.has(key)) continue;
      const bucket = this.scene.mapTileChunks.get(key);
      if (bucket) {
        if (force) {
          for (const idx of bucket) this.createMapTileObject(idx);
        } else {
          for (const idx of bucket) this.scene.tileCreateQueue.push(idx);
        }
      }
      this.scene.activeMapChunks.add(key);
    }
  }

  /**
   * Crea hasta MAP_TILES_PER_FRAME tiles de la cola por frame. Descarta índices
   * cuyo chunk ya no está activo (salió de vista antes de instanciarse).
   */
  public processTileCreateQueue(): void {
    if (this.scene.tileCreateQueue.length === 0) return;
    let created = 0;
    const budget = GameScene.MAP_TILES_PER_FRAME;
    while (this.scene.tileCreateQueue.length > 0 && created < budget) {
      const index = this.scene.tileCreateQueue.shift();
      if (index === undefined) break;
      const entry = this.scene.mapTileEntries[index];
      if (!entry) continue;
      // Si el chunk salió de vista, descartar.
      if (!this.scene.activeMapChunks.has(this.chunkKeyForTile(entry.tile.x, entry.tile.y))) {
        continue;
      }
      this.createMapTileObject(index);
      created++;
    }
  }

  /**
   * Refresco LIVIANO tras cargar las superficies REALES: destruye sólo los GameObjects de
   * tile visibles y los recrea, para que tomen la textura de superficie ya cargada, SIN
   * reconstruir el índice del mapa (`mapTileEntries`/chunks/water/shore) ni recrear
   * Minimap/MapOverlay/índices de sonido/bloqueo/agua.
   *
   * Reemplaza al viejo `reloadCurrentMap` (que llamaba `loadAndCreateMap` COMPLETO): ese
   * recorría los ~800k tiles del mundo 3 veces de más y recreaba overlays → ~1.8s de freeze
   * en la entrada, cuando lo único que cambió fueron las TEXTURAS. Ver GameScene create().
   */
  public refreshVisibleTilesForSurfaces(): void {
    // Destruir los GameObjects de tile activos (el índice mapTileEntries/chunks se conserva).
    for (const idx of Array.from(this.scene.activeTileObjects.keys())) {
      this.destroyMapTileObject(idx);
    }
    this.scene.activeTileObjects.clear();
    this.scene.roofObjects.clear();
    this.scene.waterSprites.clear();
    this.scene.activeMapChunks.clear();
    this.scene.tileCreateQueue = [];
    // Los shaders de agua no los cubre destroyMapTileObject (viven en un Map aparte): se
    // destruyen y se recrean al re-cull, igual que hacía reloadCurrentMap.
    for (const waterShader of this.scene.waterTileShaders.values()) waterShader.destroy();
    this.scene.waterTileShaders.clear();
    // Forzar recálculo de culling → recrea los chunks visibles con la textura real.
    this.scene.lastCullCamX = Number.NaN;
    this.scene.lastCullCamY = Number.NaN;
    this.updateMapCulling(true);
  }

  /** Destruye todos los GameObjects de tile activos y limpia el índice. */
  public clearMapTiles(): void {
    for (const idx of Array.from(this.scene.activeTileObjects.keys())) {
      this.destroyMapTileObject(idx);
    }
    this.scene.activeTileObjects.clear();
    this.scene.roofObjects.clear();
    this.scene.waterSprites.clear();
    this.scene.activeMapChunks.clear();
    this.scene.mapTileEntries = [];
    this.scene.waterTileSet.clear();
    this.scene.shoreBlockSet.clear();
    this.scene.mapTileChunks.clear();
    this.scene.tileCreateQueue = [];
  }
}
